import { useEffect } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { AppUseDispatch, AppUseSelector } from '../../redux/hooks'
import { SET_ALL_LANGUAGE, SET_LANGUAGE_INFO, SET_USERTYPE } from '../../redux/types'
import { getAllLanguage } from '../../redux/actions/onboardingAction'
import { STORIES } from 'routes/routeConstant'

/**
 * The chrome around the questions: language bootstrap from the URL plus the
 * auth redirects.
 *
 * @param isPostGate whether the current screen sits after the email gate — an
 *   authed user must never be bounced out of those (checkOtpAuth flips
 *   credentials.customFields.user_exist mid-flow).
 */
export const useOnboardingBootstrap = (isPostGate: boolean) => {
  const { pathname } = useLocation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = AppUseDispatch()
  const authToken = localStorage.getItem('authToken')
  const { languageInfo, languages } = AppUseSelector(({ onboarding }) => onboarding)
  const { credentials } = AppUseSelector(({ user }) => user)

  const isExistingFlow = pathname.includes('/existing')

  // `?review=1` holds the flow open for someone reviewing it while signed in.
  // Without it an existing account is bounced straight to /stories, which is
  // right for a real visitor and useless for reading through the screens. It
  // suppresses a convenience redirect only — nothing here grants access.
  const reviewMode =
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('review') === '1'

  useEffect(() => {
    if (isExistingFlow && !authToken) {
      navigate('/login')
      return
    }
    if (credentials?.customFields?.user_exist && !isExistingFlow && !isPostGate && !reviewMode) {
      navigate(STORIES)
    }
  }, [pathname, authToken, credentials, isPostGate, isExistingFlow, reviewMode, navigate])

  useEffect(() => {
    const slug = id || 'learn-chinese'
    dispatch({ type: SET_USERTYPE, payload: pathname.split('/')[2] || 'new' })

    const load = async () => {
      const languageName = (slug.split('-')[1] || 'chinese').replace(/^./, (c) => c.toUpperCase())
      let allLanguages = languages
      if (!allLanguages?.length) {
        allLanguages = await getAllLanguage()
        dispatch({ type: SET_ALL_LANGUAGE, payload: allLanguages })
      }
      if (!languageInfo || languageInfo?.language !== languageName) {
        const info = Array.isArray(allLanguages) ? allLanguages.find((l: any) => l?.language === languageName) : null
        if (info) {
          // `is_active` stays defaulted to the first preference: the placement
          // test and the result screen read it to decide which script to render
          // passages in, and a beginner never answers the writing question at
          // all. What is deliberately NOT set here is `preferenceType` — that is
          // what WritingStep draws its selected state from, and seeding it made
          // Simplified look already chosen, so people sat waiting on a question
          // they had to answer before it would advance.
          const preferences = info?.preferences?.map((p: any, i: number) => ({ ...p, is_active: i === 0 }))
          dispatch({ type: SET_LANGUAGE_INFO, payload: { ...info, preferences } })
        }
      }
    }
    load()
  }, [pathname])

  return { isExistingFlow, authToken }
}
